import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, API } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { LogoMark } from "@/components/Logo";
import { Eye, CheckCircle2, XCircle, Download } from "lucide-react";
import { LangSwitch } from "@/components/Layout";

export function CertificateCard({ cert }) {
  const { t, pick } = useLang();
  const verifyUrl = `${window.location.origin}/verify/${cert.id}`;
  return (
    <div data-testid="certificate-card" className="bg-white rounded-2xl border-2 border-slate-900 p-6 sm:p-8 max-w-xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LogoMark size={38} />
          <span className="font-heading font-bold text-slate-900">{t("appName")}</span>
        </div>
        <span className="text-xs font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full">{t("internalQualOnly")}</span>
      </div>
      <div className="text-center space-y-1 py-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("certificate")}</p>
        <h1 data-testid="cert-user-name" className="font-heading text-3xl font-extrabold text-slate-900">{cert.user_name}</h1>
        <p className="text-slate-600">{t("track")}: <span className="font-semibold">{pick(cert.track_title)}</span></p>
      </div>
      <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-4">
        <div className="text-sm space-y-1">
          <p className="text-slate-500">ID: <span data-testid="cert-id" className="font-mono font-semibold text-slate-800">{cert.id}</span></p>
          <p className="text-slate-500">{t("issuedOn")}: {new Date(cert.issued_at).toLocaleDateString()}</p>
        </div>
        <div className="text-center">
          <QRCodeSVG value={verifyUrl} size={88} data-testid="cert-qr" />
          <p className="text-[10px] text-slate-400 mt-1">Scan to verify</p>
        </div>
      </div>
      <p className="text-[11px] text-slate-400 leading-snug">{t("internalNote")}</p>
      <a data-testid="cert-download-pdf-btn" href={`${API}/certificates/${cert.id}/pdf`}
        className="w-full h-11 rounded-xl bg-teal-800 hover:bg-teal-700 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 transition-colors">
        <Download size={15} /> Download PDF
      </a>
    </div>
  );
}

export function CertificatePage() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/verify/${certId}`).then((r) => setData(r.data)).catch(() => setData({ valid: false })); }, [certId]);
  if (!data) return <p className="text-slate-500">Loading…</p>;
  if (!data.valid) return <p className="text-red-600">Certificate not found</p>;
  return <CertificateCard cert={data.certificate} />;
}

export function VerifyPage() {
  const { certId } = useParams();
  const [data, setData] = useState(null);
  const { t } = useLang();
  useEffect(() => { api.get(`/verify/${certId}`).then((r) => setData(r.data)).catch(() => setData({ valid: false })); }, [certId]);
  return (
    <div className="min-h-screen bg-[#FAF6EF] px-4 py-8 space-y-6">
      <div className="flex justify-end max-w-xl mx-auto"><LangSwitch /></div>
      <h1 className="font-heading text-2xl font-extrabold text-slate-900 text-center">{t("verifyTitle")}</h1>
      {!data ? (
        <p className="text-center text-slate-500">Loading…</p>
      ) : data.valid ? (
        <div className="space-y-4 fade-in">
          <p data-testid="verify-valid" className="flex items-center justify-center gap-2 text-emerald-700 font-semibold"><CheckCircle2 size={20} /> {t("validCert")}</p>
          <CertificateCard cert={data.certificate} />
        </div>
      ) : (
        <p data-testid="verify-invalid" className="flex items-center justify-center gap-2 text-red-600 font-semibold"><XCircle size={20} /> {t("invalidCert")}</p>
      )}
    </div>
  );
}
